import { defineStore, acceptHMRUpdate } from 'pinia';
import get from 'lodash/get';
import { useOrganizationStore } from '@/stores/organization.store';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import { Themes } from '@pendo/services/BuildingBlocks';
import { AppsService } from '@/common/apps/apps.service';
import { fetchThemes, fetchOrganizationThemes, createTheme, deleteTheme } from '@/vuex/modules/themes/themes.utils';
import { computeGuidePlatformType } from '@/utils/apps';
import { canCreateGuide } from '@/guides/helpers/common.util';

function getInitialState () {
    return {
        map: {} as Record<string, unknown>,
        areAllSubThemesLoaded: false,
    };
}

export const useThemesStore = defineStore('themes', {
    state: getInitialState,
    getters: {
        getThemeById: (state) => (id: string) => state.map[id],
        list: (state): unknown[] => values(state.map),
        listForAppId () {
            return (appId: unknown) => (this.list as Record<string, unknown>[]).filter((theme) => theme.appId === appId);
        },
        getDefaultThemeForAppId () {
            return (appId = -323232) => {
                const appThemes = this.listForAppId(appId);

                return (appThemes as Array<Record<string, string>>).find((theme) => theme.tags.toLowerCase().includes('default'));
            };
        },
        getOrgThemes (): Record<string, unknown>[] {
            return (this.list as Record<string, unknown>[]).filter((theme) => theme.orgSharedResourceSettings);
        },
        getOrgThemesForAppId () {
            return (appId: unknown) => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
                const vuexStore = require('@/vuex/store').default; // TODO: replace when apps + subscription migrate to Pinia
                const app = vuexStore?.getters['apps/getAppById'](appId);

                if (!app) return [];

                const subscription = vuexStore?.getters['subscription/getActiveSub'];

                if (!subscription) return [];

                const activeSubId = get(subscription, 'id');
                const appPlatform = computeGuidePlatformType(app.platform);

                return this.getOrgThemes.filter((theme: Record<string, unknown>) => {
                    const { orgSharedResourceSettings } = theme || {};
                    const { platform, subList = [] } = (orgSharedResourceSettings as Record<string, unknown>) || {};

                    return appPlatform === platform && (subList as string[]).map(String).includes(activeSubId);
                });
            };
        },
    },
    actions: {
        setThemes ({ themes }: { themes: unknown[] }) {
            this.map = keyBy(themes, 'id') as Record<string, unknown>;
        },
        setTheme ({ theme }: { theme: Record<string, unknown> }) {
            this.map[theme.id as string] = theme;
        },
        removeThemeFromMap ({ id }: { id: string }) {
            delete this.map[id];
        },
        setAreAllSubThemesLoaded ({ loaded }: { loaded: boolean }) {
            this.areAllSubThemesLoaded = loaded;
        },
        async loadAll ({ reFetch = false } = {}) {
            if (Object.keys(this.map).length && !reFetch) {
                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when subscription + apps migrate to Pinia
            const activeOrganization = useOrganizationStore().getActive;
            const orgId = get(activeOrganization, 'id', null);
            const activeSubscription = vuexStore?.getters['subscription/getActiveSub'];
            const subId = get(activeSubscription, 'id', null);
            const includeOrganizationResources = get(
                activeOrganization,
                'organizationFlags.enableResourceSharing',
                false,
            );

            // this will get removed in APP-22605 when backend implements an "all" query param
            const apps = vuexStore?.getters['apps/list'];
            const appThemeRequests: Promise<unknown[]>[] = [];
            const orgThemeRequests: Promise<unknown[]>[] = [];

            if (orgId && includeOrganizationResources) {
                orgThemeRequests.push(fetchOrganizationThemes(orgId, subId));
            }

            apps.filter((app: Record<string, unknown>) => app.integrated || AppsService.isLegacyApp(app))
                .forEach(({ platform, id }: { platform: string; id: unknown }) => {
                    const guidePlatformType = computeGuidePlatformType(platform);
                    const canCreate = canCreateGuide(id);

                    appThemeRequests.push(
                        (fetchThemes(id) as Promise<Array<Record<string, unknown>>>).then(async (themes) => {
                            let defaultTheme = themes.find((theme) => (theme.tags as string).toLowerCase().includes('default'));

                            if (!defaultTheme && canCreate) {
                                defaultTheme = Themes.createTheme({
                                    name: 'Default',
                                    type: 'default',
                                    platform: guidePlatformType,
                                });
                                defaultTheme!.appId = id;
                                const theme = await createTheme(defaultTheme);
                                themes.push(theme);
                            }

                            return themes;
                        }),
                    );
                });
            const allThemes = await Promise.all(appThemeRequests.concat(orgThemeRequests));

            this.setThemes({ themes: ([] as unknown[]).concat.apply([], allThemes as unknown[][]) });
            this.setAreAllSubThemesLoaded({ loaded: true });
        },
        async createTheme ({ theme }: { theme: unknown }) {
            const savedTheme = await createTheme(theme) as Record<string, unknown>;
            this.setTheme({ theme: savedTheme });

            return savedTheme.id;
        },
        async deleteTheme ({ id }: { id: string }) {
            await deleteTheme(id);
            this.removeThemeFromMap({ id });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useThemesStore, module.hot));
}
